import Mustache from 'mustache';

/**
 * @type {HTMLElement}
 */
let _$modalBody = null;

/**
 * @type {Modal[]}
 */
const _modalQueue = [];

let _modalCount = 0;

class Modal {
  constructor(){
    if(!_$modalBody){
      const $temp = document.createElement('div');
      $temp.innerHTML = document.getElementById('modal-template-body').innerHTML;
      _$modalBody = $temp.querySelector('.modal');
    }

    /**
     * @type {HTMLElement}
     */
    this.$modal = _$modalBody.cloneNode(true);
    /**
     * @type {HTMLElement}
     */
    this.$content = this.$modal.querySelector('.modal-content');
    this.$modal.setAttribute('role', 'dialog');
    this.$modal.id = 'modal-' + _modalCount;
    _modalCount++;

    // Add keyboard focus trap
    this.$modal.insertAdjacentHTML('afterbegin', '<button data-focus-trap="first" class="is-sr-only" aria-hidden="true"></button>');
    this.$modal.insertAdjacentHTML('beforeend', '<button data-focus-trap="last" class="is-sr-only" aria-hidden="true"></button>');

    /**
     * @type {HTMLElement|null}
     */
    this.$triggerElement = null;
    this.loading = true;

    this.$modal.addEventListener('focusin', (e) => {
      if (e.target.dataset.focusTrap === 'first') {
        const elements = this._getFocusableElements();
        if (elements.length > 0) {
          elements[elements.length - 1].focus();
        }
      }
      else if (e.target.dataset.focusTrap === 'last') {
        const elements = this._getFocusableElements();
        if (elements.length > 0) {
          elements[0].focus();
        }
      }
    });

    // Common data-modal-close
    this.$modal.addEventListener('click', (evt) => {
      const $el = evt.target.closest('[data-modal-close]');
      if($el){
        const navigateToUrl = $el.getAttribute('data-modal-close');
        if(navigateToUrl){
          this.loading = true;
          document.location.href = navigateToUrl;
          return;
        }

        this.close();
      }

      // Close on background click
      if (evt.target.classList.contains('modal-background')) {
        this.close();
      }
    });

    function closeOnEscape(e) {
      if (e.key === 'Escape' && this.open) {
        this.close();
      }
    }
    this._closeOnEscapeListener = closeOnEscape.bind(this);
  }

  // Properties

  get loading(){
    return this.$modal.classList.contains('-is-loading');
  }

  set loading(boolean){
    if(typeof boolean === 'boolean'){
      this.$modal.classList.toggle('-is-loading', boolean);
    }
  }

  /**
   * @return {Modal|null}
   */
  get parentModal(){
    const index = _modalQueue.indexOf(this);
    return index > 0 ? _modalQueue[index - 1] : null;
  }

  get open() {
    return this.$modal.classList.contains('is-active');
  }

  // Methods

  setTemplate(html){
    this._template = html;
  }

  show(){
    if (this.open) {
      return;
    }

    document.removeEventListener('keydown', this._closeOnEscapeListener);
    document.addEventListener('keydown', this._closeOnEscapeListener);

    // Store element that was used to open this modal if no modals are opened
    if (document.activeElement?.tagName === 'BUTTON') {
      this.$triggerElement = document.activeElement;
    }
    else {
      this.$triggerElement = null;
    }

    if(!this.$modal.parentElement){
      // Not in DOM yet > append
      document.body.appendChild(this.$modal);
    }
    if(!_modalQueue.includes(this)){
      // Not in queue yet > add
      _modalQueue.push(this);
    }
    _modalQueue.forEach(modal => {
      // Hide all modals
      modal.$modal.classList.remove('is-active');
    });
    // Show this modal
    this.$modal.classList.add('is-active');
    this._focus();
  }

  render(data){
    if(data){
      this._templateData = data;
    }
    this.$content.innerHTML = Mustache.render(this._template, this._templateData);
    this.loading = false;

    // Add title to dialog
    const title = this.$content.querySelector('h2');
    if (title) {
      title.id = title.id || `${this.$modal.id}-title`;
      this.$modal.setAttribute('aria-labelledby', title.id);
    }

    this._focus();
  }

  close(){
    // Remove from queue
    let closeModal = true;
    const index = _modalQueue.indexOf(this);

    // Call onClose if exists
    if(typeof this.onClose === 'function'){
      // If onClose returns false > Do not close this modal
      closeModal = (this.onClose() === false ? false : true);
    }

    if(closeModal === true){
      document.removeEventListener('keydown', this._closeOnEscapeListener);

      // Remove from queue
      if(index > -1){
        _modalQueue.splice(index, 1);
      }

      // Remove from DOM
      this.$modal.remove();

      const previousModal = _modalQueue.length > 0 ? _modalQueue[_modalQueue.length - 1] : null;

      // Show last modal in queue, if any
      if(previousModal){
        previousModal.show();
      }

      // Restore focus to trigger element
      if (this.$triggerElement && (!previousModal || this.$triggerElement.closest('.modal') === previousModal)) {
        this.$triggerElement.focus();
        this.$triggerElement = null;
      }
    }
  }

  setState(state, message = null){
    const $foundState = this.$content.querySelector(`[data-modal-state="${state}"]`);
    const $states = this.$content.querySelectorAll('[data-modal-state]');
    $states.forEach($state => {
      if($state.getAttribute('data-modal-state') === state || $state.querySelectorAll(`[data-modal-state="${state}"]`).length){
        $state.classList.remove('is-hidden');
        if(message && $state.getAttribute('data-modal-state') === state){
          $state.textContent = (Array.isArray(message) ? message.join('. ') : message);
        }
      } else {
        $state.classList.add('is-hidden');
      }
    });
    return ($foundState ? true : false);
  }

  /**
   * Get array of focusable elements in modal content.
   *
   * @return {HTMLElement[]} Array of elements.
   */
  _getFocusableElements() {
    let elements = Array.from(this.$content.querySelectorAll('button, input:not([type="hidden"]), textarea, select, [tabindex]:not([tabindex="-1"])'));

    function isVisible(el) {
      return el.clientWidth !== 0 && el.clientHeight !== 0;
    }

    elements = elements.filter(el => {
      if (typeof el.checkVisibility === 'function') {
        return el.checkVisibility();
      }

      return isVisible(el);
    });
    return elements;
  }

  /**
   * Focus first focusable element in modal content.
   */
  _focus(){
    const elements = this._getFocusableElements();
    if(elements.length > 0){
      elements[0].focus();
    }
  }
}

export { Modal };
